import { keyframes } from '@emotion/react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import NoData from '@root/components/common/NoData';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@root/components/ui/Chart';
import ContentBox from '@root/components/v2/content/ContentBox';
import { useTheme } from '@root/context/ThemeContext';
import { CATEGORICAL_COLORS } from '@root/utilities/pastelColors';
import { useMemo } from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, Rectangle, XAxis, YAxis } from 'recharts';
import CardTitle from './CardTitle';
import {
  bucketTooltipLabel,
  CARD_BODY_HEIGHT,
  CARD_TITLE_STYLE,
  compactNumber,
  hasSeverityValues,
  toSeverityRows,
} from './cardUtils';

// "Vulnerability findings by severity" trend card for the Kubernetes vulnerabilities dashboard.
// Renders the `get_vulnerabilities_trend` buckets as grouped Critical / High / Other bars, with the
// affected resources as a line on a right-hand axis and the legend above the plot. Other folds medium,
// low and unknown together.

// Vertical rhythm above the plot: legend row, then the "Findings" / "Resources" axis titles, then the
// first tick. recharts adds LEGEND_HEIGHT to the plot's top offset, so the titles sit AXIS_TITLE_OFFSET
// above the plot, inside the CHART_MARGIN_TOP gap below the legend.
const LEGEND_HEIGHT = 24;
const CHART_MARGIN_TOP = 30;
const AXIS_TITLE_OFFSET = 16;
const Y_AXIS_WIDTH = 40;

const SERIES = [
  { key: 'critical', label: 'Critical', color: '#FF5722' },
  { key: 'high', label: 'High', color: '#FF8A65' },
  { key: 'other', label: 'Other', color: '#FFC2AD' },
];

// Resource counts sit orders of magnitude below the findings, so they get their own axis; on the shared
// one the line would flatten onto the baseline.
const RESOURCES_SERIES = { key: 'resources', label: 'Resources', color: CATEGORICAL_COLORS.lightBlue };
const RESOURCES_AXIS_ID = 'resources';

const LEGEND_ITEMS = [...SERIES, RESOURCES_SERIES];

const CHART_CONFIG = Object.fromEntries(LEGEND_ITEMS.map(({ key, label, color }) => [key, { label, color }]));

const AXIS_TITLE_STYLE = { position: 'top', offset: AXIS_TITLE_OFFSET, fontSize: 13, fill: 'var(--text-secondary)' };

const BODY_STYLE = { height: CARD_BODY_HEIGHT, padding: '6px 15px 10px', display: 'flex', flexDirection: 'column' };

// ComposedChart hands the tooltip cursor a hairline's points instead of BarChart's hover band, so rebuild
// the band: one bucket wide, centred on the hovered bucket, spanning the plot.
const BandCursor = ({ points, top, width, height, bucketCount, className }) => {
  const bandWidth = width / bucketCount;
  return (
    <Rectangle
      className={className}
      x={points[0].x - bandWidth / 2}
      y={top + 0.5}
      width={bandWidth}
      height={height - 1}
      fillOpacity={0.4}
      pointerEvents="none"
    />
  );
};

const SKELETON_TICK_COUNT = 5;
const SKELETON_X_LABEL_HEIGHT = 22;

// MUI Skeleton's own pulse, so the SVG resources line fades in step with the Skeleton blocks around it.
const skeletonPulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
`;

// A Y axis's tick labels, hugging the plot edge they sit against.
const TickColumnSkeleton = ({ side, color }) => (
  <Box
    width={Y_AXIS_WIDTH}
    flexShrink={0}
    display="flex"
    flexDirection="column"
    justifyContent="space-between"
    alignItems={side === 'left' ? 'flex-end' : 'flex-start'}
    paddingLeft={side === 'right' ? '8px' : 0}
    paddingRight={side === 'left' ? '8px' : 0}
    paddingBottom={`${SKELETON_X_LABEL_HEIGHT}px`}>
    {Array.from({ length: SKELETON_TICK_COUNT }, (_, index) => (
      <Skeleton key={index} variant="text" width={18} height={12} sx={{ bgcolor: color }} />
    ))}
  </Box>
);

// Mirrors the loaded body (legend row, axis titles, then grouped bars under the resources line between the
// two tick columns) so nothing jumps when data lands. The 2px + 16px + 12px between legend and plot add up
// to CHART_MARGIN_TOP, and the titles sit half an axis in, where the real ones anchor.
const TrendSkeleton = () => {
  const { isDarkMode } = useTheme();
  const skeletonColor = isDarkMode ? '#2d3748' : undefined;
  // Bar heights are % of the plot; the line's points are % from its top, kept above the bars so it reads.
  const heights = [45, 65, 40, 58, 70, 50, 62];
  const linePoints = [22, 16, 20, 12, 14, 8, 12]
    .map((y, index) => `${((index + 0.5) / heights.length) * 100},${y}`)
    .join(' ');

  return (
    <Box sx={BODY_STYLE}>
      <Box display="flex" gap="16px" height={LEGEND_HEIGHT} alignItems="center">
        {LEGEND_ITEMS.map(series => (
          <Skeleton key={series.key} variant="text" width={56} height={16} sx={{ bgcolor: skeletonColor }} />
        ))}
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        height="16px"
        marginTop="2px"
        paddingX={`${Y_AXIS_WIDTH / 2}px`}>
        <Skeleton variant="text" width={50} height={16} sx={{ bgcolor: skeletonColor }} />
        <Skeleton variant="text" width={62} height={16} sx={{ bgcolor: skeletonColor }} />
      </Box>
      <Box flex={1} minHeight={0} display="flex" marginTop="12px">
        <TickColumnSkeleton side="left" color={skeletonColor} />
        <Box flex={1} minWidth={0} display="flex" flexDirection="column">
          <Box flex={1} position="relative" display="flex" alignItems="flex-end" justifyContent="space-around">
            {heights.map((height, index) => (
              <Box key={index} display="flex" alignItems="flex-end" gap="3px" height="100%">
                {SERIES.map((series, seriesIndex) => (
                  <Skeleton
                    key={series.key}
                    variant="rectangular"
                    width={8}
                    height={`${Math.max(15, height - seriesIndex * 18)}%`}
                    sx={{ borderRadius: '2px 2px 0 0', bgcolor: skeletonColor }}
                  />
                ))}
              </Box>
            ))}
            <Box
              component="svg"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                overflow: 'visible',
                color: theme => skeletonColor ?? alpha(theme.palette.text.primary, 0.11),
                animation: `${skeletonPulse} 2s ease-in-out 0.5s infinite`,
              }}>
              <polyline
                points={linePoints}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </Box>
          </Box>
          <Box height={SKELETON_X_LABEL_HEIGHT} display="flex" alignItems="flex-end" justifyContent="space-around">
            {heights.map((_, index) => (
              <Skeleton key={index} variant="text" width={28} height={12} sx={{ bgcolor: skeletonColor }} />
            ))}
          </Box>
        </Box>
        <TickColumnSkeleton side="right" color={skeletonColor} />
      </Box>
    </Box>
  );
};

const VulnerabilityFindingsTrend = ({ data, isLoading = false, timeFilter, height = '300px' }) => {
  const rows = useMemo(() => toSeverityRows(data, timeFilter === '24h'), [data, timeFilter]);
  // Zero-filled buckets (no scans, or nothing found) would draw an empty plot, so treat them as no data.
  const hasFindings = useMemo(() => hasSeverityValues(rows), [rows]);

  let body;
  if (isLoading) {
    body = <TrendSkeleton />;
  } else if (!hasFindings) {
    body = (
      <Box sx={BODY_STYLE} justifyContent="center">
        <NoData message="No Vulnerability findings found" imageheight="60px" imageWidth="60px" />
      </Box>
    );
  } else {
    body = (
      <Box sx={BODY_STYLE}>
        <ChartContainer config={CHART_CONFIG} className="aspect-auto h-full w-full">
          <ComposedChart
            data={rows}
            margin={{ top: CHART_MARGIN_TOP, right: 4, left: 0, bottom: 0 }}
            barGap={2}
            barCategoryGap="25%">
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval="preserveStartEnd"
              minTickGap={16}
              tick={{ fontSize: 11 }}
            />
            <YAxis
              width={Y_AXIS_WIDTH}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tick={{ fontSize: 11 }}
              tickFormatter={value => compactNumber.format(value)}
              // `top` centres the title over the axis; shift it back to start at the card's left edge.
              label={{ ...AXIS_TITLE_STYLE, value: 'Findings', textAnchor: 'start', dx: Y_AXIS_WIDTH / 30 }}
            />
            <YAxis
              yAxisId={RESOURCES_AXIS_ID}
              orientation="right"
              width={Y_AXIS_WIDTH}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tick={{ fontSize: 11 }}
              tickFormatter={value => compactNumber.format(value)}
              // Mirrors the Findings title: anchored at its end so it finishes at the card's right edge.
              label={{ ...AXIS_TITLE_STYLE, value: 'Resources', textAnchor: 'end', dx: -Y_AXIS_WIDTH / 30 }}
            />
            <ChartTooltip
              cursor={<BandCursor bucketCount={rows.length} />}
              content={<ChartTooltipContent labelFormatter={bucketTooltipLabel} />}
            />
            {/* Pinned to the top edge with a fixed height: recharts adds that height to the plot's
                top offset, so the axis title always lands in the gap between legend and plot. */}
            <ChartLegend
              verticalAlign="top"
              align="left"
              height={LEGEND_HEIGHT}
              wrapperStyle={{ top: 0, left: 0 }}
              content={<ChartLegendContent className="justify-start py-0 text-[12px]" />}
            />
            {SERIES.map(series => (
              <Bar key={series.key} dataKey={series.key} fill={series.color} radius={[2, 2, 0, 0]} maxBarSize={10} />
            ))}
            <Line
              yAxisId={RESOURCES_AXIS_ID}
              dataKey={RESOURCES_SERIES.key}
              type="linear"
              stroke={RESOURCES_SERIES.color}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3 }}
            />
          </ComposedChart>
        </ChartContainer>
      </Box>
    );
  }

  return (
    <ContentBox
      title={<CardTitle title="Vulnerability findings" />}
      titleContainerStyle={CARD_TITLE_STYLE}
      containerClassName={'border-box-no-pad'}
      paddingMode="content"
      height={height}>
      {body}
    </ContentBox>
  );
};

export default VulnerabilityFindingsTrend;
